import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import countries from 'i18n-iso-countries';

/**
 * useLogisticsRate — fetch estimated shipping rate for a product.
 *
 * SECURITY NOTE: The original code put the logistics auth token in the
 * frontend bundle via REACT_APP_LOGISTICS_TOKEN. This is visible to anyone
 * who opens DevTools. The proper fix is to proxy through your own backend
 * so the token stays server-side. Until that's built, this hook still uses
 * the env var but FLAGS it as a known security issue.
 *
 * The original code also hardcoded the delivery address. This version
 * accepts a delivery address parameter so callers pass the real user
 * location.
 */
const LOGISTICS_URL =
    (typeof process !== 'undefined' && process.env?.REACT_APP_LOGISTICS_BASE_URL) ||
    null; /* No localhost fallback — fail loudly if env missing */

const LOGISTICS_TOKEN =
    typeof process !== 'undefined' ? process.env?.REACT_APP_LOGISTICS_TOKEN : undefined;

const dlog = (...args) => {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[logistics]', ...args);
    }
};

const useLogisticsRate = (product, deliveryAddress) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const productIdRef = useRef(null);

    useEffect(() => {
        const productId = product?.item_id;
        if (!productId) return;

        /* Skip refetch for the same product */
        if (productIdRef.current === productId && data) return;
        productIdRef.current = productId;

        if (!LOGISTICS_URL) {
            dlog('REACT_APP_LOGISTICS_BASE_URL not set — skipping logistics fetch');
            return;
        }

        const addressStr = product.custom_field_hash?.cf_line_address || '';
        if (!addressStr) {
            dlog('product has no cf_line_address — skipping logistics fetch');
            return;
        }

        let cancelled = false;
        const controller = new AbortController();

        const fetchRate = async () => {
            try {
                setLoading(true);
                setError(null);

                const pickupCity     = product.custom_field_hash?.cf_city || '';
                const pickupState    = product.custom_field_hash?.cf_state || '';
                const pickupCountry  = countries.getAlpha2Code(
                    product.custom_field_hash?.cf_country || 'Nigeria',
                    'en'
                );

                const dest = deliveryAddress ?? {};
                const destCountry = countries.getAlpha2Code(dest.country || 'Nigeria', 'en');

                const payload = {
                    origin_city:      pickupCity,
                    destination_city: dest.city || pickupCity,
                    transport_mode:   'road',
                    service_level:    'Standard',
                    weight:           parseFloat(product.cf_weight) || 1,
                    pickup_address: {
                        line1:        product.custom_field_hash?.cf_line_address || '',
                        city:         pickupCity,
                        state:        pickupState,
                        country:      pickupCountry,
                        phone:        product.custom_field_hash?.cf_vendor_phone_no || '',
                        contact_name: product.vendor_name || 'Vendor',
                    },
                    delivery_address: {
                        line1:   dest.line1 || '',
                        city:    dest.city  || '',
                        state:   dest.state || '',
                        country: destCountry,
                        phone:   dest.phone || '',
                    },
                    items: [{
                        name:     product.name,
                        weight:   parseFloat(product.cf_weight) || 1,
                        price:    Number(product.rate) || 0,
                        quantity: 1,
                    }],
                };

                const res = await axios.post(
                    `${LOGISTICS_URL}/routes/match`,
                    payload,
                    {
                        headers: LOGISTICS_TOKEN ? { authorization: `Bearer ${LOGISTICS_TOKEN}` } : {},
                        signal: controller.signal,
                    }
                );

                if (cancelled) return;

                const matched = res.data?.status === 'success' ? res.data?.data?.[0] : null;
                if (matched) setData(matched);
            } catch (err) {
                if (cancelled || axios.isCancel?.(err) || err?.name === 'CanceledError') return;
                dlog('match failed', err);
                setError(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchRate();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [product?.item_id, deliveryAddress?.city, deliveryAddress?.state, deliveryAddress?.country]);

    return { data, loading, error };
};

export default useLogisticsRate;